import { FFT } from "./fft";
import type { Lane, NoteEvent, Section, TrackData } from "../types";

const FFT_SIZE = 1024;
const HOP = 512;
const TARGET_SR = 22050;

/** Band edges in Hz: bass < 250, mid 250–2200, high > 2200. */
const BAND_EDGES = [250, 2200];

const GRAY_RATIO = 0.12;

export type ProgressFn = (stage: string, value: number) => void;

/**
 * Master seed: FNV-1a hash over the audio content (strided samples +
 * duration). The same song always hashes to the same seed, and every
 * stochastic generation decision derives from one PRNG seeded with it.
 */
function hashAudio(samples: Float32Array): number {
  let h = 0x811c9dc5;
  const stride = Math.max(1, Math.floor(samples.length / 65536));
  for (let i = 0; i < samples.length; i += stride) {
    // Quantize to 12 bits so float noise does not break determinism.
    const q = Math.round(samples[i]! * 2048) & 0xfff;
    h ^= q;
    h = Math.imul(h, 0x01000193);
  }
  h ^= samples.length;
  h = Math.imul(h, 0x01000193);
  return h >>> 0;
}

/** Deterministic PRNG so the same song always yields the same track. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function movingAverage(src: Float32Array, radius: number): Float32Array {
  const n = src.length;
  const out = new Float32Array(n);
  let sum = 0;
  let count = 0;
  // Sliding window prefix approach
  const prefix = new Float64Array(n + 1);
  for (let i = 0; i < n; i++) prefix[i + 1] = prefix[i]! + src[i]!;
  for (let i = 0; i < n; i++) {
    const a = Math.max(0, i - radius);
    const b = Math.min(n - 1, i + radius);
    sum = prefix[b + 1]! - prefix[a]!;
    count = b - a + 1;
    out[i] = sum / count;
  }
  return out;
}

function percentile(sorted: Float32Array, p: number): number {
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.floor(p * (sorted.length - 1))));
  return sorted[idx]!;
}

/** Mixes an AudioBuffer down to mono and decimates near TARGET_SR. */
function toMono(buffer: AudioBuffer): { samples: Float32Array; sampleRate: number } {
  const decim = Math.max(1, Math.round(buffer.sampleRate / TARGET_SR));
  const sampleRate = buffer.sampleRate / decim;
  const outLen = Math.floor(buffer.length / decim);
  const samples = new Float32Array(outLen);
  const chs: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) chs.push(buffer.getChannelData(c));
  const invCh = 1 / chs.length;
  for (let i = 0; i < outLen; i++) {
    let acc = 0;
    const base = i * decim;
    for (let d = 0; d < decim; d++) {
      let s = 0;
      for (const ch of chs) s += ch[base + d]!;
      acc += s * invCh;
    }
    samples[i] = acc / decim;
  }
  return { samples, sampleRate };
}

function estimateBpm(flux: Float32Array, frameRate: number): number {
  const minLag = Math.round((60 / 190) * frameRate);
  const maxLag = Math.round((60 / 60) * frameRate);
  let bestLag = minLag;
  let bestVal = -Infinity;
  const n = flux.length;
  for (let lag = minLag; lag <= maxLag; lag++) {
    let acc = 0;
    for (let i = 0; i + lag < n; i += 2) acc += flux[i]! * flux[i + lag]!;
    if (acc > bestVal) {
      bestVal = acc;
      bestLag = lag;
    }
  }
  return Math.round((60 * frameRate) / bestLag);
}

const yieldToUi = () => new Promise<void>((r) => setTimeout(r, 0));

/**
 * Offline Audiosurf-style analysis: intensity envelope → slopes,
 * spectral flux onsets → circles, dominant band → lane, sections → mood.
 */
export async function analyzeAudio(buffer: AudioBuffer, onProgress: ProgressFn): Promise<TrackData> {
  onProgress("Preparing audio", 0);
  const { samples, sampleRate } = toMono(buffer);
  if (samples.length < FFT_SIZE * 4) {
    throw new Error("That audio file is too short to build a track.");
  }
  const seed = hashAudio(samples);
  const frameRate = sampleRate / HOP;
  const numFrames = Math.max(1, Math.floor((samples.length - FFT_SIZE) / HOP));
  const bins = FFT_SIZE / 2;
  const binHz = sampleRate / FFT_SIZE;
  const bandEnd0 = Math.round(BAND_EDGES[0]! / binHz);
  const bandEnd1 = Math.round(BAND_EDGES[1]! / binHz);

  const fft = new FFT(FFT_SIZE);
  const window = new Float32Array(FFT_SIZE);
  for (let i = 0; i < FFT_SIZE; i++) window[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (FFT_SIZE - 1)));

  const frame = new Float32Array(FFT_SIZE);
  const mags = new Float32Array(bins);
  const prevMags = new Float32Array(bins);

  const rms = new Float32Array(numFrames);
  const flux = new Float32Array(numFrames);
  const bandFlux = [new Float32Array(numFrames), new Float32Array(numFrames), new Float32Array(numFrames)];

  await yieldToUi();
  for (let f = 0; f < numFrames; f++) {
    const off = f * HOP;
    let energy = 0;
    for (let i = 0; i < FFT_SIZE; i++) {
      const s = samples[off + i]! * window[i]!;
      frame[i] = s;
      energy += s * s;
    }
    rms[f] = Math.sqrt(energy / FFT_SIZE);
    fft.magnitudes(frame, mags);
    let fb = 0;
    let fm = 0;
    let fh = 0;
    for (let b = 1; b < bins; b++) {
      const d = mags[b]! - prevMags[b]!;
      if (d > 0) {
        if (b < bandEnd0) fb += d;
        else if (b < bandEnd1) fm += d;
        else fh += d;
      }
      prevMags[b] = mags[b]!;
    }
    bandFlux[0]![f] = fb;
    bandFlux[1]![f] = fm;
    bandFlux[2]![f] = fh;
    flux[f] = fb + fm + fh;
    if ((f & 511) === 0) {
      onProgress("Listening to the song", 0.05 + 0.75 * (f / numFrames));
      await yieldToUi();
    }
  }

  onProgress("Shaping the track", 0.82);
  await yieldToUi();

  // --- Intensity envelope: smoothed RMS normalized by percentiles ---
  const smoothRadius = Math.round(0.7 * frameRate); // ~1.4s window — reactive hills
  const smoothed = movingAverage(rms, smoothRadius);
  const sortedRms = new Float32Array(smoothed).sort();
  const lo = percentile(sortedRms, 0.08);
  const hi = percentile(sortedRms, 0.95);
  const range = Math.max(1e-6, hi - lo);
  const intensity = new Float32Array(numFrames);
  for (let i = 0; i < numFrames; i++) {
    intensity[i] = Math.min(1, Math.max(0, (smoothed[i]! - lo) / range));
  }

  // --- Height: integrate slope (calm = uphill, intense = downhill), detrend ---
  const rawHeight = new Float32Array(numFrames);
  let h = 0;
  for (let i = 0; i < numFrames; i++) {
    h += 0.5 - intensity[i]!;
    rawHeight[i] = h;
  }
  const trend = movingAverage(rawHeight, Math.round(10 * frameRate));
  let maxAbs = 1e-6;
  const height = new Float32Array(numFrames);
  for (let i = 0; i < numFrames; i++) {
    height[i] = rawHeight[i]! - trend[i]!;
    maxAbs = Math.max(maxAbs, Math.abs(height[i]!));
  }
  for (let i = 0; i < numFrames; i++) height[i] = height[i]! / maxAbs;
  // Gentle final smoothing so the ride has no jitter
  const heightSmooth = movingAverage(height, Math.round(0.25 * frameRate));

  // --- Sections: tiered intensity with hysteresis ---
  const sections: Section[] = [];
  const tierOf = (v: number, prev: number): 0 | 1 | 2 => {
    const upA = 0.42;
    const upB = 0.7;
    const downA = 0.34;
    const downB = 0.62;
    if (prev <= 0) return v > upA ? (v > upB ? 2 : 1) : 0;
    if (prev === 1) return v > upB ? 2 : v < downA ? 0 : 1;
    return v < downB ? (v < downA ? 0 : 1) : 2;
  };
  const minSectionFrames = Math.round(4 * frameRate);
  let secStart = 0;
  let curTier = tierOf(intensity[0]!, -1);
  for (let i = 1; i < numFrames; i++) {
    const t = tierOf(intensity[i]!, curTier);
    if (t !== curTier && i - secStart >= minSectionFrames) {
      let acc = 0;
      for (let j = secStart; j < i; j++) acc += intensity[j]!;
      sections.push({ t0: secStart / frameRate, t1: i / frameRate, intensity: acc / (i - secStart), tier: curTier });
      secStart = i;
      curTier = t;
    }
  }
  {
    let acc = 0;
    for (let j = secStart; j < numFrames; j++) acc += intensity[j]!;
    sections.push({
      t0: secStart / frameRate,
      t1: numFrames / frameRate,
      intensity: acc / Math.max(1, numFrames - secStart),
      tier: curTier,
    });
  }

  // --- Onset detection: adaptive-threshold peak picking on total flux ---
  onProgress("Placing circles", 0.9);
  await yieldToUi();
  const fluxMean = movingAverage(flux, Math.round(0.5 * frameRate));
  let globalMean = 0;
  for (let i = 0; i < numFrames; i++) globalMean += flux[i]!;
  globalMean /= numFrames;

  const minGap = Math.round(0.22 * frameRate);
  const onsetIdx: number[] = [];
  const onsetStrength: number[] = [];
  let last = -minGap;
  for (let i = 2; i < numFrames - 2; i++) {
    const v = flux[i]!;
    const threshold = fluxMean[i]! * 1.5 + globalMean * 0.12;
    if (
      v > threshold &&
      v >= flux[i - 1]! &&
      v >= flux[i + 1]! &&
      v > flux[i - 2]! &&
      v > flux[i + 2]! &&
      i - last >= minGap
    ) {
      onsetIdx.push(i);
      onsetStrength.push(v - fluxMean[i]!);
      last = i;
    }
  }
  let maxStrength = 1e-6;
  for (const s of onsetStrength) maxStrength = Math.max(maxStrength, s);

  const rand = mulberry32(seed);
  const notes: NoteEvent[] = [];
  let prevGray = false;
  for (let k = 0; k < onsetIdx.length; k++) {
    const i = onsetIdx[k]!;
    const time = (i * HOP) / sampleRate;
    if (time < 2 || time > buffer.duration - 1.5) continue;
    const fb = bandFlux[0]![i]! + bandFlux[0]![Math.min(numFrames - 1, i + 1)]!;
    const fm = bandFlux[1]![i]! + bandFlux[1]![Math.min(numFrames - 1, i + 1)]!;
    const fh = (bandFlux[2]![i]! + bandFlux[2]![Math.min(numFrames - 1, i + 1)]!) * 1.4; // highs are quieter
    const lane: Lane = fb >= fm && fb >= fh ? 0 : fm >= fh ? 1 : 2;
    const gray: boolean = !prevGray && rand() < GRAY_RATIO;
    prevGray = gray;
    notes.push({
      time,
      lane,
      kind: gray ? "gray" : "color",
      slot: rand() < 0.22 ? "accent" : "primary",
      strength: Math.min(1, onsetStrength[k]! / maxStrength),
    });
  }

  onProgress("Done", 1);
  return {
    duration: buffer.duration,
    seed,
    frameRate,
    bpmEstimate: estimateBpm(flux, frameRate),
    intensity,
    height: heightSmooth,
    sections,
    notes,
  };
}


